import { api } from './client'
import type { AlertItem } from './rapoarte'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Financiar/FacturiModels.cs ----------

export type InvoiceKind = 'Proforma' | 'Fiscala' | 'NotaCredit'
export type InvoiceStatus = 'Draft' | 'Emisa' | 'Stornata'

export interface InvoiceLine {
  id: string
  lineNumber: number
  articleId: string
  articleCode: string
  description: string
  unitOfMeasureLabel: string
  quantityThousands: number
  unitPricePer1000: number
  discountPercent: number
  vatRateId: string | null
  vatRatePercent: number
  lotNumber: string | null
  productionOrderId: string | null
  netAmount: number
  vatAmount: number
  totalAmount: number
}

export interface InvoiceListItem {
  id: string
  number: string
  kind: InvoiceKind
  status: InvoiceStatus
  issueDate: string
  dueDate: string
  clientId: string
  clientName: string
  currency: string
  total: number
  paidAt: string | null
  overdueDays: number | null
}

export interface InvoiceDetail {
  id: string
  number: string
  kind: InvoiceKind
  status: InvoiceStatus
  issueDate: string
  dueDate: string
  clientId: string
  clientName: string
  deliveryAddress: string
  billingAddress: string
  clientPoReference: string | null
  paymentTermDays: number | null
  currency: string
  exchangeRate: number
  orderId: string | null
  orderNumber: string | null
  shipmentId: string | null
  shipmentNumber: string | null
  sourceOfferId: string | null
  relatedInvoiceId: string | null
  relatedInvoiceNumber: string | null
  isStorno: boolean
  paidAt: string | null
  lines: InvoiceLine[]
  netTotal: number
  vatTotal: number
  total: number
}

export interface GenerateInvoicePayload { issueDate: string | null; dueDate: string | null }

export type SaveInvoiceLinePayload = Pick<
  InvoiceLine, 'articleId' | 'quantityThousands' | 'unitPricePer1000' | 'discountPercent' | 'vatRateId' | 'lotNumber' | 'productionOrderId'
>

export interface UpdateInvoicePayload {
  issueDate: string
  dueDate: string
  exchangeRate: number
  lines: SaveInvoiceLinePayload[]
}

export interface CreditNoteLinePayload { originalLineId: string; quantityThousands: number }

export interface PendingApprovalResult { pendingApproval: boolean }

export interface AgingBucket { label: string; minDays: number; maxDays: number | null; invoices: InvoiceListItem[]; total: number }
export interface AgingReport { buckets: AgingBucket[] }

export interface CompanySettings {
  id: string
  name: string
  cui: string | null
  regCom: string | null
  address: string | null
  city: string | null
  county: string | null
  country: string | null
  phone: string | null
  email: string | null
  bankNameRON: string | null
  ibanRON: string | null
  bankNameEUR: string | null
  ibanEUR: string | null
}

export type SaveCompanySettingsPayload = Omit<CompanySettings, 'id'>

export interface InvoiceListFilters { from?: string; to?: string; clientId?: string; agentUserId?: string }

export const listInvoices = (filters?: InvoiceListFilters) =>
  api.get<InvoiceListItem[]>('/financiar/facturi', { params: filters }).then((r) => r.data)
export const getInvoice = (id: string) => api.get<InvoiceDetail>(`/financiar/facturi/${id}`).then((r) => r.data)
export const getAgingReport = () => api.get<AgingReport>('/financiar/facturi/restante').then((r) => r.data)
export const getInvoicesExportUrl = (filters?: InvoiceListFilters) =>
  api.get<Blob>('/financiar/facturi/export', { params: filters, responseType: 'blob' }).then((r) => r.data)
export const getAgingReportExportUrl = () =>
  api.get<Blob>('/financiar/facturi/restante/export', { responseType: 'blob' }).then((r) => r.data)

export interface SalesJournalEntry { invoiceNumber: string; issueDate: string; clientName: string; vatRatePercent: number; netAmount: number; vatAmount: number; totalAmount: number }
export interface VatRateSummary { vatRatePercent: number; netAmount: number; vatAmount: number; totalAmount: number }
export interface SalesJournal { from: string; to: string; entries: SalesJournalEntry[]; summary: VatRateSummary[] }

export const getSalesJournal = (from: string, to: string) =>
  api.get<SalesJournal>('/financiar/facturi/jurnal-vanzari', { params: { from, to } }).then((r) => r.data)
export const getSalesJournalExportUrl = (from: string, to: string) =>
  api.get<Blob>('/financiar/facturi/jurnal-vanzari/export', { params: { from, to }, responseType: 'blob' }).then((r) => r.data)

export const generateInvoiceFromOrder = (orderId: string, payload: GenerateInvoicePayload) =>
  api.post<{ id: string }>(`/financiar/facturi/genereaza-din-comanda/${orderId}`, payload).then((r) => r.data)
export const generateInvoiceFromShipment = (shipmentId: string, payload: GenerateInvoicePayload) =>
  api.post<{ id: string }>(`/financiar/facturi/genereaza-din-expeditie/${shipmentId}`, payload).then((r) => r.data)
export const generateProforma = (orderId: string, payload: GenerateInvoicePayload) =>
  api.post<{ id: string }>(`/financiar/facturi/genereaza-proforma/${orderId}`, payload).then((r) => r.data)

export const updateInvoice = (id: string, payload: UpdateInvoicePayload) => api.put(`/financiar/facturi/${id}`, payload)
export const issueInvoice = (id: string) => api.post(`/financiar/facturi/${id}/emite`)
export const markInvoicePaid = (id: string) => api.post(`/financiar/facturi/${id}/marcheaza-platita`)
export const stornoInvoice = (id: string, reason: string) =>
  api.post<PendingApprovalResult>(`/financiar/facturi/${id}/storno`, { reason }).then((r) => r.data)
export const creditNoteInvoice = (id: string, reason: string, lines: CreditNoteLinePayload[]) =>
  api.post<PendingApprovalResult>(`/financiar/facturi/${id}/nota-credit`, { reason, lines }).then((r) => r.data)

export const getInvoicePdf = (id: string) =>
  api.get<Blob>(`/financiar/facturi/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data)
export const sendInvoiceEmail = (id: string) => api.post(`/financiar/facturi/${id}/trimite-email`)

export const getCompanySettings = () => api.get<CompanySettings>('/financiar/setari').then((r) => r.data)
export const updateCompanySettings = (payload: SaveCompanySettingsPayload) =>
  api.put<CompanySettings>('/financiar/setari', payload).then((r) => r.data)

// ---------- Partea II — Încasări, plăți și avansuri (FR-FCF-001…006) ----------

export type CashAccountKind = 'Bancar' | 'Casierie'

export interface CashAccount {
  id: string
  name: string
  kind: CashAccountKind
  currency: string
  iban: string | null
  isActive: boolean
  balance: number
}
export type SaveCashAccountPayload = Omit<CashAccount, 'id' | 'balance'>

export const listCashAccounts = () => api.get<CashAccount[]>('/financiar/conturi').then((r) => r.data)
export const createCashAccount = (payload: SaveCashAccountPayload) =>
  api.post<{ id: string }>('/financiar/conturi', payload).then((r) => r.data)
export const updateCashAccount = (id: string, payload: SaveCashAccountPayload) => api.put(`/financiar/conturi/${id}`, payload)

export interface OpenInvoice { invoiceId: string; number: string; dueDate: string; total: number; outstanding: number }
export interface OpenSupplierInvoice { supplierInvoiceId: string; number: string; dueDate: string; total: number; outstanding: number }
export interface PartnerAdvanceBalance { balance: number }

export interface ReceiptAllocation { id: string; invoiceId: string; invoiceNumber: string; allocatedAmount: number }
export interface ReceiptListItem {
  id: string; number: string; date: string; clientId: string; clientName: string
  cashAccountId: string; cashAccountName: string; currency: string; amount: number; allocatedAmount: number; cancelledAt: string | null
}
export interface ReceiptDetail extends ReceiptListItem {
  exchangeRate: number; notes: string | null; allocations: ReceiptAllocation[]
}
export interface SaveReceiptLinePayload { invoiceId: string; amount: number }
export interface SaveReceiptPayload {
  date: string; clientId: string; cashAccountId: string; currency: string; exchangeRate: number
  amount: number; notes: string | null; lines: SaveReceiptLinePayload[]
}

export const listReceipts = () => api.get<ReceiptListItem[]>('/financiar/incasari').then((r) => r.data)
export const getReceipt = (id: string) => api.get<ReceiptDetail>(`/financiar/incasari/${id}`).then((r) => r.data)
export const getClientOpenInvoices = (clientId: string) =>
  api.get<OpenInvoice[]>(`/financiar/incasari/clienti/${clientId}/facturi-deschise`).then((r) => r.data)
export const getClientAdvanceBalance = (clientId: string) =>
  api.get<PartnerAdvanceBalance>(`/financiar/incasari/clienti/${clientId}/sold-avans`).then((r) => r.data)
export const createReceipt = (payload: SaveReceiptPayload) =>
  api.post<{ id: string }>('/financiar/incasari', payload).then((r) => r.data)
export const allocateReceipt = (id: string, lines: SaveReceiptLinePayload[]) =>
  api.post(`/financiar/incasari/${id}/aloca`, { lines })
export const cancelReceipt = (id: string) => api.post(`/financiar/incasari/${id}/anuleaza`)

export type PaymentStatus = 'Draft' | 'Executata' | 'Anulata'

export interface PaymentAllocation {
  id: string; supplierInvoiceId: string | null; supplierInvoiceNumber: string | null
  expenseReportId: string | null; expenseReportNumber: string | null; allocatedAmount: number
}
export interface PaymentListItem {
  id: string; number: string; date: string; supplierId: string | null; supplierName: string | null
  employeeUserId: string | null; employeeName: string | null
  cashAccountId: string; cashAccountName: string; currency: string; amount: number; status: PaymentStatus; isRecurring: boolean
}
export interface PaymentDetail extends PaymentListItem {
  neededByDate: string | null
  exchangeRate: number; category: string | null; notes: string | null; executedAt: string | null
  recurringScheduleId: string | null; expenseReportId: string | null; allocations: PaymentAllocation[]
}
export interface SavePaymentLinePayload { supplierInvoiceId: string; amount: number }
export interface SavePaymentPayload {
  date: string; supplierId: string; cashAccountId: string; currency: string; exchangeRate: number
  amount: number; category: string | null; notes: string | null; lines: SavePaymentLinePayload[]
}
export interface RequestEmployeeAdvancePayload {
  date: string; cashAccountId: string; currency: string; exchangeRate: number
  amount: number; category: string | null; notes: string | null; neededByDate: string | null
}
export interface ExecutePaymentResult { pendingApproval: boolean }

export const listPayments = () => api.get<PaymentListItem[]>('/financiar/plati').then((r) => r.data)
export const getPayment = (id: string) => api.get<PaymentDetail>(`/financiar/plati/${id}`).then((r) => r.data)
export const getSupplierOpenInvoices = (supplierId: string) =>
  api.get<OpenSupplierInvoice[]>(`/financiar/plati/furnizori/${supplierId}/facturi-deschise`).then((r) => r.data)
export const getSupplierAdvanceBalance = (supplierId: string) =>
  api.get<PartnerAdvanceBalance>(`/financiar/plati/furnizori/${supplierId}/sold-avans`).then((r) => r.data)
export const createPayment = (payload: SavePaymentPayload) =>
  api.post<{ id: string }>('/financiar/plati', payload).then((r) => r.data)
export const allocatePayment = (id: string, lines: SavePaymentLinePayload[]) =>
  api.post(`/financiar/plati/${id}/aloca`, { lines })
export const executePayment = (id: string) => api.post<ExecutePaymentResult>(`/financiar/plati/${id}/executa`).then((r) => r.data)
export const cancelPayment = (id: string) => api.post(`/financiar/plati/${id}/anuleaza`)

export const listMyAdvances = () => api.get<PaymentListItem[]>('/financiar/plati/mele').then((r) => r.data)
export const requestEmployeeAdvance = (payload: RequestEmployeeAdvancePayload) =>
  api.post<{ id: string }>('/financiar/plati/avans-angajat', payload).then((r) => r.data)

export type PaymentIntervalType = 'Lunar' | 'Trimestrial' | 'Semestrial' | 'Anual'

export interface RecurringPaymentSchedule {
  id: string; description: string; supplierId: string | null; supplierName: string | null
  amount: number; currency: string; cashAccountId: string; cashAccountName: string
  category: string | null; intervalType: PaymentIntervalType; nextDueDate: string; isActive: boolean
}
export type SaveRecurringPaymentSchedulePayload = Omit<RecurringPaymentSchedule, 'id' | 'supplierName' | 'cashAccountName'>

export const listRecurringPaymentSchedules = () => api.get<RecurringPaymentSchedule[]>('/financiar/plati/programari').then((r) => r.data)
export const createRecurringPaymentSchedule = (payload: SaveRecurringPaymentSchedulePayload) =>
  api.post<{ id: string }>('/financiar/plati/programari', payload).then((r) => r.data)
export const updateRecurringPaymentSchedule = (id: string, payload: SaveRecurringPaymentSchedulePayload) =>
  api.put(`/financiar/plati/programari/${id}`, payload)

// ---------- Cash-flow: alerte de scadență + prognoză (FR-FCF-007…009) ----------

export const getCashflowAlerts = () => api.get<AlertItem[]>('/financiar/cashflow/alerte').then((r) => r.data)

export type CashflowHorizon = 'Saptamanal' | 'Lunar' | 'Trimestrial'

export interface CashflowBucket {
  periodStart: string; periodEnd: string; label: string
  cashInEstimat: number; cashOutEstimat: number; soldNet: number
}
export interface CashflowForecast { horizon: CashflowHorizon; soldInitial: number; buckets: CashflowBucket[] }

export const getCashflowForecast = (horizon: CashflowHorizon = 'Lunar') =>
  api.get<CashflowForecast>('/financiar/cashflow/prognoza', { params: { horizon } }).then((r) => r.data)
